import posthog from 'posthog-js';
import { createPrefixedRandomID } from './random-id';

type PostHogProperties = Record<string, unknown>;

type InitCueboardPostHogOptions = {
  surface: string;
  runtime: string;
  platform?: string;
  entrySurface?: string;
  properties?: PostHogProperties;
};

type CueboardObservabilitySeverity = 'info' | 'warning' | 'error' | 'critical';

type CueboardObservabilityEvent = {
  failureClass: string;
  severity?: CueboardObservabilitySeverity;
  surface: string;
  service?: string;
  operationId?: string;
  requestId?: string;
  traceId?: string;
  spanId?: string;
  provider?: string;
  platform?: string;
  entrySurface?: string;
  integrationProvider?: string;
  integrationSurface?: string;
  statusCode?: number;
  networkErrorKind?: string;
  runbookId?: string;
  diagnosisHint?: string;
  error?: unknown;
  properties?: PostHogProperties;
};

type CueboardStepEvent = {
  stepName: string;
  stepState: string;
  surface: string;
  service?: string;
  operationId?: string;
  requestId?: string;
  traceId?: string;
  spanId?: string;
  sessionId?: string;
  agentId?: string;
  platform?: string;
  entrySurface?: string;
  integrationProvider?: string;
  integrationSurface?: string;
  runbookId?: string;
  properties?: PostHogProperties;
};

type CueboardFeatureOutcomeEvent = {
  featureKey: string;
  success?: boolean;
  outcome?: 'success' | 'failure';
  surface: string;
  service?: string;
  operationId?: string;
  requestId?: string;
  traceId?: string;
  spanId?: string;
  sessionId?: string;
  agentId?: string;
  provider?: string;
  platform?: string;
  entrySurface?: string;
  integrationProvider?: string;
  integrationSurface?: string;
  clientSource?: string;
  clientVersion?: string;
  clientBuild?: string;
  clientPlatform?: string;
  clientOSVersion?: string;
  toolName?: string;
  statusCode?: number;
  durationMs?: number;
  failureClass?: string;
  error?: unknown;
  errorMessage?: string;
  properties?: PostHogProperties;
};

type CueboardIdentityUser = {
  id: string;
  email?: string | null;
  display_name?: string | null;
  created_at?: string | null;
};

type IdentifyCueboardPostHogUserOptions = {
  source: string;
  surface: string;
  runtime?: string;
  platform?: string;
  entrySurface?: string;
};

type CueboardPostHogState = {
  initialized: boolean;
  globalErrorHandlersInstalled: boolean;
  pendingIdentify?: {
    user: CueboardIdentityUser;
    options: IdentifyCueboardPostHogUserOptions;
  };
};

type CueboardGlobal = typeof globalThis & {
  __cueboardPostHogState?: CueboardPostHogState;
};

const DEFAULT_POSTHOG_API_KEY =
  'phc_RZSlZNwaLrXVV8uh7HpQjAql5ccicci935X7uzATynK';
const DEFAULT_POSTHOG_API_HOST = 'https://us.i.posthog.com';
const OBSERVABILITY_EVENT_SCHEMA_VERSION = 1;
const OBSERVABILITY_EVENT_NAME = 'cueboard_observability_event';
const FEATURE_OUTCOME_EVENT_NAME = 'cueboard_feature_outcome_event';
const EMAIL_PATTERN = /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/gi;
const URI_CREDENTIAL_PATTERN =
  /\b([a-z][a-z0-9+.-]*:\/\/)([^:@/\s]+):([^@/\s]+)@/gi;
const AUTH_VALUE_PATTERN =
  /\b(bearer|basic)\s+[a-z0-9._~+/-]+=*|\b(api[_-]?key|token|secret|password|authorization|cookie)=([^&\s]+)/gi;
const LOCAL_PATH_PATTERN = /(?:file:\/\/)?\/(?:Users|home)\/[^\s)]+/gi;
const CARD_LAST4_PATTERN =
  /\b(card|last4|last four|ending in)\s*[:#]?\s*\d{4}\b/gi;

function shouldDisableBrowserPostHog() {
  if (typeof window === 'undefined') {
    return true;
  }
  const hostname = window.location.hostname.toLowerCase();
  return (
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
  );
}

function getGlobalState() {
  const globalScope = globalThis as CueboardGlobal;
  if (!globalScope.__cueboardPostHogState) {
    globalScope.__cueboardPostHogState = {
      initialized: false,
      globalErrorHandlersInstalled: false,
    };
  }
  return globalScope.__cueboardPostHogState;
}

export function initCueboardPostHog({
  surface,
  runtime,
  platform,
  entrySurface,
  properties = {},
}: InitCueboardPostHogOptions) {
  if (shouldDisableBrowserPostHog()) {
    return false;
  }

  const state = getGlobalState();
  if (!state.initialized) {
    posthog.init(DEFAULT_POSTHOG_API_KEY, {
      api_host: DEFAULT_POSTHOG_API_HOST,
      autocapture: false,
      capture_pageview: false,
      disable_session_recording: true,
      persistence: 'localStorage',
    });
    state.initialized = true;
  }
  installGlobalErrorHandlers(surface);

  posthog.register({
    app: 'cueboard-web',
    runtime,
    platform: normalizeTelemetryToken(platform) || inferClientPlatform(runtime),
    surface,
    entry_surface:
      normalizeTelemetryToken(entrySurface) ||
      inferEntrySurface(surface, runtime),
    ...properties,
  });
  flushPendingIdentify();

  return true;
}

export function identifyCueboardPostHogUser(
  user: CueboardIdentityUser,
  {
    source,
    surface,
    runtime = 'browser',
    platform,
    entrySurface,
  }: IdentifyCueboardPostHogUserOptions
) {
  if (!user?.id?.trim()) {
    return false;
  }
  const state = getGlobalState();
  if (!state.initialized) {
    state.pendingIdentify = {
      user,
      options: { source, surface, runtime, platform, entrySurface },
    };
    return false;
  }

  const normalizedPlatform =
    normalizeTelemetryToken(platform) || inferClientPlatform(runtime);
  const normalizedEntrySurface =
    normalizeTelemetryToken(entrySurface) ||
    inferEntrySurface(surface, runtime);
  const trimmedEmail = user.email?.trim();
  const trimmedName = user.display_name?.trim();
  const userProperties: PostHogProperties = {
    user_id: user.id,
    identity_source: source,
    platform: normalizedPlatform,
    entry_surface: normalizedEntrySurface,
    last_seen_platform: normalizedPlatform,
    last_seen_surface: normalizedEntrySurface,
  };
  const userPropertiesSetOnce: PostHogProperties = {
    first_seen_platform: normalizedPlatform,
    first_seen_surface: normalizedEntrySurface,
    first_identity_source: source,
  };

  if (trimmedEmail) {
    userProperties.email = trimmedEmail;
    userProperties.$email = trimmedEmail;
  }
  if (trimmedName) {
    userProperties.name = trimmedName;
    userProperties.display_name = trimmedName;
  }
  if (user.created_at?.trim()) {
    userProperties.account_created_at = user.created_at.trim();
    userPropertiesSetOnce.account_created_at = user.created_at.trim();
  }

  posthog.identify(user.id, userProperties, userPropertiesSetOnce);
  return true;
}

function flushPendingIdentify() {
  const state = getGlobalState();
  const pending = state.pendingIdentify;
  if (!pending) {
    return;
  }
  state.pendingIdentify = undefined;
  identifyCueboardPostHogUser(pending.user, pending.options);
}

export function captureCueboardPostHogEvent(
  eventName: string,
  properties: PostHogProperties = {}
) {
  if (!getGlobalState().initialized) {
    return false;
  }

  posthog.capture(eventName, properties);
  return true;
}

export function captureCueboardObservabilityEvent({
  failureClass,
  severity = 'error',
  surface,
  service = 'cueboard-web',
  operationId,
  requestId,
  traceId,
  spanId,
  provider,
  platform,
  entrySurface,
  integrationProvider,
  integrationSurface,
  statusCode,
  networkErrorKind,
  runbookId,
  diagnosisHint,
  error,
  properties = {},
}: CueboardObservabilityEvent) {
  if (!getGlobalState().initialized) {
    return false;
  }

  const observabilityEventId = randomID();
  const normalizedOperationId =
    operationId || requestId || traceId || observabilityEventId;
  const parsedError = normalizeError(error);
  const customProperties = scrubProperties(properties);
  const customErrorFingerprint =
    typeof customProperties.surface_error_fingerprint === 'string'
      ? customProperties.surface_error_fingerprint
      : undefined;
  delete customProperties.error_fingerprint;
  const eventProperties = {
    ...customProperties,
    event_schema_version: OBSERVABILITY_EVENT_SCHEMA_VERSION,
    observability_event_id: observabilityEventId,
    operation_id: normalizedOperationId,
    request_id: requestId || '',
    trace_id: traceId || '',
    span_id: spanId || '',
    env: inferEnvironment(),
    service,
    failure_class: failureClass,
    severity,
    surface,
    platform:
      normalizeTelemetryToken(platform) ||
      normalizeTelemetryToken(customProperties.platform) ||
      inferClientPlatformFromSurface(surface),
    entry_surface:
      normalizeTelemetryToken(entrySurface) ||
      stringProperty(customProperties.entry_surface) ||
      inferEntrySurface(surface),
    integration_provider:
      normalizeTelemetryToken(integrationProvider) ||
      stringProperty(customProperties.integration_provider) ||
      '',
    integration_surface:
      normalizeTelemetryToken(integrationSurface) ||
      stringProperty(customProperties.integration_surface) ||
      '',
    surface_error_fingerprint:
      customErrorFingerprint ||
      fingerprint([failureClass, service, surface, parsedError.message]),
    provider: provider || '',
    status_code: statusCode || 0,
    network_error_kind: networkErrorKind || '',
    runbook_id: runbookId || '',
    diagnosis_hint: diagnosisHint || '',
    error_message: parsedError.message,
    error_stack: parsedError.stack,
    suspect_layer: customProperties.suspect_layer || 'client',
  };
  posthog.capture(OBSERVABILITY_EVENT_NAME, eventProperties);
  if (error) {
    posthog.captureException(error, {
      ...eventProperties,
      observability_event_name: OBSERVABILITY_EVENT_NAME,
    });
  }
  return true;
}

export function captureCueboardStepEvent({
  stepName,
  stepState,
  surface,
  service = 'cueboard-web',
  operationId,
  requestId,
  traceId,
  spanId,
  sessionId,
  agentId,
  platform,
  entrySurface,
  integrationProvider,
  integrationSurface,
  runbookId,
  properties = {},
}: CueboardStepEvent) {
  if (!getGlobalState().initialized) {
    return false;
  }

  const observabilityEventId = randomID();
  const customProperties = scrubProperties(properties);
  posthog.capture('cueboard_step_event', {
    ...customProperties,
    event_schema_version: OBSERVABILITY_EVENT_SCHEMA_VERSION,
    observability_event_id: observabilityEventId,
    operation_id: operationId || requestId || traceId || observabilityEventId,
    request_id: requestId || '',
    trace_id: traceId || '',
    span_id: spanId || '',
    env: inferEnvironment(),
    service,
    surface,
    platform:
      normalizeTelemetryToken(platform) ||
      normalizeTelemetryToken(customProperties.platform) ||
      inferClientPlatformFromSurface(surface),
    entry_surface:
      normalizeTelemetryToken(entrySurface) ||
      stringProperty(customProperties.entry_surface) ||
      inferEntrySurface(surface),
    integration_provider:
      normalizeTelemetryToken(integrationProvider) ||
      stringProperty(customProperties.integration_provider) ||
      '',
    integration_surface:
      normalizeTelemetryToken(integrationSurface) ||
      stringProperty(customProperties.integration_surface) ||
      '',
    step_name: stepName,
    step_state: stepState,
    session_id: sessionId || '',
    agent_id: agentId || '',
    runbook_id: runbookId || '',
  });
  return true;
}

export function captureCueboardFeatureOutcomeEvent({
  featureKey,
  success,
  outcome,
  surface,
  service = 'cueboard-web',
  operationId,
  requestId,
  traceId,
  spanId,
  sessionId,
  agentId,
  provider,
  platform,
  entrySurface,
  integrationProvider,
  integrationSurface,
  clientSource,
  clientVersion,
  clientBuild,
  clientPlatform,
  clientOSVersion,
  toolName,
  statusCode,
  durationMs,
  failureClass,
  error,
  errorMessage,
  properties = {},
}: CueboardFeatureOutcomeEvent) {
  if (!getGlobalState().initialized) {
    return false;
  }

  const featureEventId = randomID();
  const customProperties = scrubProperties(properties);
  const parsedError = normalizeError(error ?? errorMessage);
  const normalizedOutcome =
    outcome === 'success' || success === true ? 'success' : 'failure';
  const normalizedSuccess = normalizedOutcome === 'success';
  const sanitizedErrorMessage = normalizedSuccess
    ? ''
    : truncate(
        scrubStringValue(errorMessage || parsedError.message || ''),
        768
      );
  posthog.capture(FEATURE_OUTCOME_EVENT_NAME, {
    ...customProperties,
    event_schema_version: OBSERVABILITY_EVENT_SCHEMA_VERSION,
    observability_event_id: featureEventId,
    feature_event_id: featureEventId,
    operation_id: operationId || requestId || traceId || featureEventId,
    request_id: requestId || '',
    trace_id: traceId || '',
    span_id: spanId || '',
    env: inferEnvironment(),
    service,
    feature_key: normalizeTelemetryToken(featureKey),
    surface,
    user_id: stringProperty(customProperties.user_id),
    session_id: sessionId || stringProperty(customProperties.session_id),
    agent_id: agentId || stringProperty(customProperties.agent_id),
    provider: provider || '',
    platform:
      normalizeTelemetryToken(platform) ||
      normalizeTelemetryToken(customProperties.platform) ||
      inferClientPlatformFromSurface(surface),
    entry_surface:
      normalizeTelemetryToken(entrySurface) ||
      stringProperty(customProperties.entry_surface) ||
      inferEntrySurface(surface),
    integration_provider:
      normalizeTelemetryToken(integrationProvider) ||
      stringProperty(customProperties.integration_provider) ||
      '',
    integration_surface:
      normalizeTelemetryToken(integrationSurface) ||
      stringProperty(customProperties.integration_surface) ||
      '',
    client_source:
      clientSourceProperty(clientSource) ||
      clientSourceProperty(customProperties.client_source) ||
      'web',
    client_version:
      trimmedStringProperty(clientVersion) ||
      trimmedStringProperty(customProperties.client_version),
    client_build:
      trimmedStringProperty(clientBuild) ||
      trimmedStringProperty(customProperties.client_build),
    client_platform:
      normalizeTelemetryToken(clientPlatform) ||
      normalizeTelemetryToken(customProperties.client_platform) ||
      inferClientPlatformFromSurface(surface),
    client_os_version:
      trimmedStringProperty(clientOSVersion) ||
      trimmedStringProperty(customProperties.client_os_version),
    tool_name: normalizeTelemetryToken(toolName),
    outcome: normalizedOutcome,
    success: normalizedSuccess,
    status_code: statusCode || 0,
    duration_ms: durationMs || 0,
    failure_class: normalizedSuccess ? '' : failureClass || '',
    surface_error_fingerprint: normalizedSuccess
      ? ''
      : fingerprint([
          featureKey,
          service,
          surface,
          toolName,
          failureClass,
          sanitizedErrorMessage,
        ]),
    suspect_layer: customProperties.suspect_layer || '',
    route_class: customProperties.route_class || '',
    error_message: sanitizedErrorMessage,
  });
  return true;
}

function installGlobalErrorHandlers(surface: string) {
  const state = getGlobalState();
  if (state.globalErrorHandlersInstalled || typeof window === 'undefined') {
    return;
  }
  window.addEventListener('error', event => {
    captureCueboardObservabilityEvent({
      failureClass: 'client.unhandled_error',
      severity: 'error',
      surface,
      error: event.error || event.message,
      properties: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });
  window.addEventListener('unhandledrejection', event => {
    captureCueboardObservabilityEvent({
      failureClass: 'client.unhandled_error',
      severity: 'error',
      surface,
      error: event.reason,
      properties: {
        error_kind: 'unhandledrejection',
      },
    });
  });
  state.globalErrorHandlersInstalled = true;
}

function normalizeError(error: unknown): { message: string; stack: string } {
  if (error instanceof Error) {
    return {
      message: truncate(
        scrubStringValue(error.message || error.name || 'unknown error'),
        768
      ),
      stack: truncate(scrubStringValue(error.stack || ''), 2000),
    };
  }
  if (typeof error === 'string') {
    return { message: truncate(scrubStringValue(error), 768), stack: '' };
  }
  if (error == null) {
    return { message: '', stack: '' };
  }
  return { message: truncate(scrubStringValue(String(error)), 768), stack: '' };
}

function inferEnvironment() {
  if (typeof window === 'undefined') {
    return '';
  }
  const host = window.location.hostname.toLowerCase();
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'development';
  }
  if (host.includes('staging')) {
    return 'staging';
  }
  return 'production';
}

function inferClientPlatform(runtime = '') {
  const normalized = normalizeTelemetryToken(runtime);
  if (normalized === 'swift_webview') {
    return 'macos';
  }
  if (normalized === 'browser') {
    return 'web';
  }
  return normalized || 'web';
}

function inferClientPlatformFromSurface(surface: string) {
  const normalized = normalizeTelemetryToken(surface);
  if (normalized.startsWith('macos') || normalized.startsWith('cueboard_app')) {
    return 'macos';
  }
  if (normalized.startsWith('website')) {
    return 'web';
  }
  return 'web';
}

function inferEntrySurface(surface: string, runtime = '') {
  const normalizedSurface = normalizeTelemetryToken(surface);
  const normalizedRuntime = normalizeTelemetryToken(runtime);
  if (
    normalizedSurface === 'embedded_chat' &&
    normalizedRuntime === 'swift_webview'
  ) {
    return 'cueboard_app_chat';
  }
  if (normalizedSurface === 'website') {
    return 'cueboard_web_app';
  }
  return normalizedSurface || surface;
}

function stringProperty(value: unknown) {
  return typeof value === 'string' ? normalizeTelemetryToken(value) : '';
}

function trimmedStringProperty(value: unknown) {
  return typeof value === 'string'
    ? truncate(scrubStringValue(value.trim()), 128)
    : '';
}

function clientSourceProperty(value: unknown) {
  switch (normalizeTelemetryToken(value)) {
    case 'web':
    case 'website':
    case 'browser':
      return 'web';
    case 'macos':
    case 'macos_desktop':
    case 'cueboard_app':
    case 'bridge_app':
      return 'macos_desktop';
    case 'discord':
    case 'discord_bot':
    case 'discord_dm':
    case 'discord_guild':
      return 'discord';
    case 'imessage':
    case 'imessage_bot':
    case 'imessage_dm':
    case 'ios_messages':
    case 'messages':
      return 'imessage';
    case 'wechat':
    case 'wechat_bot':
    case 'we_chat':
    case 'weixin':
      return 'wechat';
    case 'telegram':
    case 'telegram_bot':
    case 'telegram_dm':
      return 'telegram';
    default:
      return '';
  }
}

function normalizeTelemetryToken(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }
  return value
    .trim()
    .toLowerCase()
    .replace(/[\s/]+/g, '_')
    .replace(/[^a-z0-9_.-]/g, '')
    .replace(/^[_\-.]+|[_\-.]+$/g, '');
}

export function sanitizeTelemetryRoute(path: string) {
  const withoutQuery = path.split('?')[0]?.split('#')[0] ?? path;
  let sanitized = withoutQuery.trim();
  if (!sanitized) {
    return '/';
  }
  sanitized = sanitized
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
      ':id'
    )
    .replace(/\b\d{5,}\b/g, ':n');
  return sanitized.startsWith('/') ? sanitized : `/${sanitized}`;
}

function randomID() {
  return createPrefixedRandomID('obs');
}

function fingerprint(parts: unknown[]) {
  const normalized = parts
    .map(part =>
      truncate(
        String(part ?? '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' '),
        256
      )
    )
    .join('|');
  return hashFingerprintValue(normalized);
}

function hashFingerprintValue(value: string) {
  let hash1 = 0x811c9dc5;
  let hash2 = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    hash1 ^= code;
    hash1 = Math.imul(hash1, 0x01000193);
    hash2 ^= code;
    hash2 = Math.imul(hash2, 0x01000193);
    hash2 ^= hash2 >>> 16;
  }
  const part1 = (hash1 >>> 0).toString(16).padStart(8, '0');
  const part2 = (hash2 >>> 0).toString(16).padStart(8, '0');
  return `${part1}${part2}`;
}

function scrubProperties(properties: PostHogProperties) {
  const scrubbed: PostHogProperties = {};
  for (const [key, value] of Object.entries(properties)) {
    scrubbed[key] = scrubValue(key, value);
  }
  return scrubbed;
}

function scrubValue(key: string, value: unknown): unknown {
  if (isSensitiveKey(key)) {
    return '[redacted]';
  }
  if (Array.isArray(value)) {
    return value.map(item => scrubValue('', item));
  }
  if (isPlainRecord(value)) {
    return scrubProperties(value);
  }
  if (typeof value === 'string') {
    return truncate(scrubStringValue(value), 2000);
  }
  return value;
}

function isPlainRecord(value: unknown): value is PostHogProperties {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function isSensitiveKey(key: string) {
  const normalized = key.toLowerCase();
  return [
    'token',
    'secret',
    'password',
    'authorization',
    'cookie',
    'api_key',
    'apikey',
  ].some(marker => normalized.includes(marker));
}

function truncate(value: string, max: number) {
  return value.length <= max ? value : value.slice(0, max);
}

function scrubStringValue(value: string) {
  return value
    .replace(URI_CREDENTIAL_PATTERN, '$1[redacted]:[redacted]@')
    .replace(AUTH_VALUE_PATTERN, match => {
      const lower = match.toLowerCase();
      if (lower.startsWith('bearer ')) {
        return 'Bearer [redacted]';
      }
      if (lower.startsWith('basic ')) {
        return 'Basic [redacted]';
      }
      const separator = match.indexOf('=');
      return separator >= 0
        ? `${match.slice(0, separator + 1)}[redacted]`
        : '[redacted]';
    })
    .replace(EMAIL_PATTERN, '[redacted-email]')
    .replace(LOCAL_PATH_PATTERN, '[redacted-path]')
    .replace(CARD_LAST4_PATTERN, '$1 [redacted]');
}
